import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Keeps the instalment amount ("Effectuer 3 paiements de X") on the price the customer is reading.
 *
 * Liquid computes it from `product.price`, which is wrong twice over: discounts come from Sales
 * Genius, which swaps the contents of the `[data-sg-key]` wrapper for its own `<syde-price>` *after*
 * the server render, and the figure is the product's price rather than the selected variant's.
 *
 * The displayed price is read as the **lowest** money value visible inside the wrapper, not by class
 * name: once Sales Genius has injected, the markup in there is the app's, and a compare-at price
 * always sits above the price actually charged. `innerText` is what makes "visible" literal — it
 * skips whatever CSS hides, which is how the theme's own `price__hidden` variant is discarded.
 *
 * Same body-level observer as jd-sales-genius.js, and for the same reason: the injection is not
 * announced by any event, and a variant change re-renders the price subtree. Writes are guarded by a
 * value comparison, so the pass our own write triggers settles immediately.
 */
const MONEY_PATTERN = /\d[\d\s., ']*\d|\d/g;

function readDisplayedPrice(source, currency) {
  const text = source.innerText;

  if (!text) return null;

  const amounts = (text.match(MONEY_PATTERN) ?? [])
    .map((raw) => convertMoneyToMinorUnits(raw, currency))
    .filter((amount) => typeof amount === 'number' && amount > 0);

  return amounts.length ? Math.min(...amounts) : null;
}

function updateLine(line) {
  const target = line.querySelector('[data-jd-installment-amount]');
  const source = document.querySelector(line.dataset.jdPriceSource);
  const count = Number(line.dataset.jdInstallments);
  const { currency, moneyFormat } = line.dataset;

  if (!target || !source || !(count > 0) || !currency || !moneyFormat) return;

  const price = readDisplayedPrice(source, currency);

  if (price === null) return;

  // Floor, not round: Liquid's `divided_by` truncates, so an undiscounted product keeps the exact
  // figure the server rendered and nothing visibly changes on load.
  const amount = formatMoney(Math.floor(price / count), moneyFormat, currency);

  if (amount !== target.textContent) target.textContent = amount;
}

function updateAll() {
  for (const line of document.querySelectorAll('[data-jd-installments][data-jd-price-source]')) {
    updateLine(line);
  }
}

let scheduled = false;

function schedule() {
  if (scheduled) return;
  scheduled = true;

  requestAnimationFrame(() => {
    scheduled = false;
    updateAll();
  });
}

new MutationObserver(schedule).observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
});

schedule();
